import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { themeToCssVars } from '@mlm/shared';
import { getTenantConfig, tenantLogoUrl } from './api';
import { hostLogoOverride } from './host-logo';

/**
 * Applies the tenant's theme tokens as inline CSS custom properties on <html>
 * at boot. Inline element styles outrank any stylesheet rule, so the tenant
 * theme overrides the baseline CSS (`member-theme.css`, `@mlm/ui/styles.css`)
 * without those files being edited.
 *
 * Tokens the tenant omits are simply not set, so they fall through to the
 * baseline stylesheet — an un-themed deployment looks exactly like the demo.
 *
 * Reuses the shared `['tenant-config']` query (dedup'd with the rest of the
 * app), so this adds no extra network request.
 *
 * NOTE: this themes surfaces that read `:root` vars (member portal shell,
 * admin, cashier). The public storefront defines its own vars scoped under
 * `.omni-store`, which a `:root` override does not reach — per-tenant
 * storefront theming is a deliberate follow-up (see docs/TENANT_CUSTOMIZATION.md).
 */
export function useTenantTheme() {
  const { data } = useQuery({
    queryKey: ['tenant-config'],
    queryFn: getTenantConfig,
    retry: 0,
  });
  const theme = data?.theme ?? null;
  useEffect(() => {
    const root = document.documentElement;
    const vars = themeToCssVars(theme);
    for (const [name, value] of vars) root.style.setProperty(name, value);

    // White-label favicon: a per-hostname override (multi-domain tenants) wins,
    // then a dedicated uploaded favicon, then the uploaded logo, then a pasted
    // logo URL (falls back to the static index.html /favicon.svg when none is
    // set). The index.html baked favicon is host-agnostic, so on an overridden
    // host the tab icon swaps here after boot (brief, acceptable).
    const faviconUrl =
      hostLogoOverride() ??
      (theme?.faviconKey
        ? `${tenantLogoUrl('favicon')}?v=${encodeURIComponent(theme.faviconKey)}`
        : theme?.logoKey
          ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
          : (theme?.logoUrl ?? null));
    if (faviconUrl) {
      const icon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (icon) icon.href = faviconUrl;
    }

    // Load the tenant's font stylesheets (Google Fonts etc.) so the `--font-*`
    // vars resolve. The baseline Hanken <link> in index.html stays as the
    // un-themed fallback; these are ADDED on top for a themed tenant.
    const links: HTMLLinkElement[] = [];
    for (const href of theme?.fontLinks ?? []) {
      if (typeof href !== 'string' || href.trim() === '') continue;
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.dataset.tenantFont = '';
      document.head.appendChild(el);
      links.push(el);
    }

    return () => {
      for (const [name] of vars) root.style.removeProperty(name);
      for (const el of links) el.remove();
    };
  }, [theme]);
}
